import { EventType } from '@portal/enum/analytics';
import ErrorsMessages from '@portal/enum/errors';
import { FileTypePayable, PayableType } from '@portal/models/enumerators';
import PayableApi from '@portal/repositories/payable';
import { sendEvent } from '@portal/services/firebase';
import { translate } from '@portal/services/i18n';
import { AnyAction, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import * as MessageService from '~/services/message';
import {
  PAYABLE_GROUP_REPORT,
  PAYABLE_PREVIEW,
  PAYABLE_REPORT,
} from './actionTypes';
import { decreaseLoading, increaseLoading, LoadingAction } from './loading';

export type GetPayableGroupAction = {
  type: string;
  payload: models.PaginationResponse<models.PayableGroup>;
};

export type GetPayableAction = {
  type: string;
  payload: models.PaginationResponse<models.Payable>;
};

export type GetPayablePreviewAction = {
  type: string;
  payload: models.PayablePreview | null;
};

export type GetPayableApprovesToExecute = {
  type: string;
  payload: number | null;
};

export type GetExportedResponseCnabAction = {
  type: string;
  payload: models.Payable | null;
};

export const getPayableGroups =
  (paymentAccountId: string, searchParams?: advancedFilterModels.baseFilter) =>
  async (dispatch: Dispatch<GetPayableGroupAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const payload: models.PaginationResponse<models.PayableGroup> =
        await PayableApi.getPayableGroups(
          {
            ...(searchParams
              ? {
                  ...searchParams,
                  offset: searchParams.page,
                  limit: searchParams.pageSize,
                }
              : {
                  pageSize: 10,
                  page: 1,
                  orderBy: 'createdAt',
                  sort: 'desc',
                }),
            offset: searchParams?.page,
            limit: searchParams?.pageSize,
          },
          paymentAccountId
        );
      dispatch({
        payload,
        type: PAYABLE_GROUP_REPORT,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getPayablesByGroupId =
  (
    id: string,
    paymentAccountId: string,
    searchParams: advancedFilterModels.PaymentsFileAdvancedFilter
  ) =>
  async (dispatch: Dispatch<GetPayableAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const payload: models.PaginationResponse<models.Payable> =
        await PayableApi.getPayables(id, paymentAccountId, {
          ...searchParams,
          offset: searchParams?.page,
          limit: searchParams?.pageSize,
        });

      dispatch({
        payload,
        type: PAYABLE_REPORT,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const uploadAndPreviewPayables =
  (
    file: File,
    fileExtension: string,
    fileName: string,
    fileType: FileTypePayable,
    paymentAccountId: string
  ) =>
  async (dispatch: Dispatch<GetPayablePreviewAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const payableBucket = await PayableApi.getUrl({
        fileExtension,
      });

      await PayableApi.uploadFile(file, fileExtension, payableBucket.url);

      const completeParams = {
        bucket: payableBucket.bucket,
        name: fileName,
        fileType: fileType,
      };

      const completePayload = await PayableApi.getPreview(
        completeParams,
        paymentAccountId
      );

      const payload = {
        ...completePayload,
        bucket: payableBucket.bucket,
      };

      dispatch({
        payload,
        type: PAYABLE_PREVIEW,
      });
    } catch (err) {
      if (err instanceof Error) {
        if (
          err.message.split(',')[0] &&
          err.message.split(',')[0].toUpperCase() ===
            ErrorsMessages.INSUFFICIENT_FUNDS
        ) {
          MessageService.error(err.message.split(',')[0], {
            originalError: err,
          });
        } else {
          MessageService.error(err.message, { originalError: err });
        }
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const cleanPreview = () => ({
  payload: null,
  type: PAYABLE_PREVIEW,
});

export const createPayable =
  (
    fileBucket: string,
    fileName: string,
    pin: string,
    paymentAccountId: string,
    fileType?: FileTypePayable,
    transactionsType?: PayableType,
    referenceDate?: string
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | GetPayableGroupAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const completeParams = {
        bucket: fileBucket,
        name: fileName,
        authenticate: {
          pin,
        },
        fileType,
        type: transactionsType,
        referenceDate,
      };

      await PayableApi.createFile(completeParams, paymentAccountId);
      MessageService.success(
        'PAGES.PAYMENT.GENERATE_BY_FILE.MESSAGE.SUCCESS_PAY_BY_LOT'
      );
      sendEvent(EventType.PAYABLE_FILE_ADDED);
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getPayablesPreview =
  (
    bucket: string,
    filename: string,
    paymentAccountId: string,
    fileType?: FileTypePayable
  ) =>
  async (dispatch: Dispatch<GetPayablePreviewAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const completePayload = await PayableApi.getPreview(
        {
          bucket,
          name: filename,
          fileType: fileType,
        },
        paymentAccountId
      );
      const payload = {
        ...completePayload,
        bucket,
      };

      dispatch({
        payload,
        type: PAYABLE_PREVIEW,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const approvePayableGroup =
  (
    id: string,
    pin: string,
    paymentAccountId: string,
    searchParams?: advancedFilterModels.baseFilter
  ): ThunkAction<void, reducers.rootReducer, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await PayableApi.approvePayableGroup(id, pin, paymentAccountId);
      dispatch(getPayableGroups(paymentAccountId, searchParams));
      sendEvent(EventType.PAYABLE_APPROVE_GROUP);
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const reprovePayableGroup =
  (
    id: string,
    pin: string,
    paymentAccountId: string,
    searchParams?: advancedFilterModels.baseFilter
  ): ThunkAction<void, reducers.rootReducer, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await PayableApi.reprovePayableGroup(id, pin, paymentAccountId);
      dispatch(getPayableGroups(paymentAccountId, searchParams));
      sendEvent(EventType.PAYABLE_REPROVE_GROUP);
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const bulkApprovePayableGroups =
  (
    groupIds: string[],
    pin: string,
    paymentAccountId: string,
    searchParams?: advancedFilterModels.baseFilter
  ): ThunkAction<void, reducers.rootReducer, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await PayableApi.bulkApprovePayableGroups(
        groupIds,
        pin,
        paymentAccountId
      );
      dispatch(getPayableGroups(paymentAccountId, searchParams));
      sendEvent(EventType.PAYABLE_APPROVE_GROUP);
      MessageService.success(
        groupIds.length === 1
          ? 'PAGES.PAYMENT.ACCOUNT_PAYABLE.BATCH.APPROVE_SUCCESS_SINGLE'
          : translate(
              'PAGES.PAYMENT.ACCOUNT_PAYABLE.BATCH.APPROVE_SUCCESS_MULTIPLE',
              { count: groupIds.length }
            )
      );
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const bulkReprovePayableGroups =
  (
    groupIds: string[],
    pin: string,
    paymentAccountId: string,
    searchParams?: advancedFilterModels.baseFilter
  ): ThunkAction<void, reducers.rootReducer, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await PayableApi.bulkReprovePayableGroups(
        groupIds,
        pin,
        paymentAccountId
      );
      dispatch(getPayableGroups(paymentAccountId, searchParams));
      sendEvent(EventType.PAYABLE_REPROVE_GROUP);
      MessageService.success(
        groupIds.length === 1
          ? translate(
              'PAGES.PAYMENT.ACCOUNT_PAYABLE.BATCH.REPROVE_SUCCESS_SINGLE'
            )
          : translate(
              'PAGES.PAYMENT.ACCOUNT_PAYABLE.BATCH.REPROVE_SUCCESS_MULTIPLE',
              { count: groupIds.length }
            )
      );
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getExportedResponseCnab =
  (id: string) =>
  async (dispatch: Dispatch<GetExportedResponseCnabAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const responseCnabUrl = await PayableApi.getExportedResponseCnab(
        id,
        false
      );

      if (responseCnabUrl.url) {
        window.location.assign(responseCnabUrl.url);
      } else {
        throw new Error(ErrorsMessages.UNAVAILABLE_RETURN_FILE);
      }
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const downloadPaymentExtract =
  (groupId: string, params: advancedFilterModels.baseFilter) =>
  async (dispatch: Dispatch) => {
    dispatch(increaseLoading());
    try {
      const response = await PayableApi.downloadPaymentExtract(groupId, params);

      if (response.statementUrl) {
        window.location.assign(response.statementUrl);
      } else {
        throw new Error(ErrorsMessages.UNAVAILABLE_RETURN_FILE);
      }
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const downloadIndividualStatement =
  (id: string) => async (dispatch: Dispatch) => {
    dispatch(increaseLoading());
    try {
      const response = await PayableApi.downloadIndividualStatement(id);

      if (response.statementUrl) {
        window.location.assign(response.statementUrl);
      } else {
        throw new Error(ErrorsMessages.UNAVAILABLE_RETURN_FILE);
      }
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const downloadBatchPaymentStatements =
  (groupId: string) => async (dispatch: Dispatch) => {
    dispatch(increaseLoading());
    try {
      await PayableApi.downloadBatchPaymentStatements(groupId);

      MessageService.success(
        'PAGES.EMPLOYEES.EMPLOYEES_PAYMENT.PAYMENT_DETAILS.EXPORT.BATCH_STATEMENTS_EMAIL'
      );
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };
